import React from 'react';
import { Clock, CheckCircle, Eye, Star, XCircle, Calendar, TrendingUp } from 'lucide-react';
import { JobApplication } from '../../services/applicationService';

interface ApplicationStatusCardProps {
  application: JobApplication;
  onClick?: () => void;
}

const ApplicationStatusCard: React.FC<ApplicationStatusCardProps> = ({ application, onClick }) => {
  // Status configuration
  const statusConfig = {
    applied: {
      icon: Clock,
      color: 'text-blue-600 bg-blue-50 border-blue-200',
      label: 'Applied',
      bgGradient: 'from-blue-50 to-blue-100'
    },
    test_completed: {
      icon: CheckCircle,
      color: 'text-green-600 bg-green-50 border-green-200',
      label: 'Test Completed',
      bgGradient: 'from-green-50 to-green-100'
    },
    under_review: {
      icon: Eye,
      color: 'text-purple-600 bg-purple-50 border-purple-200',
      label: 'Under Review',
      bgGradient: 'from-purple-50 to-purple-100'
    },
    shortlisted: {
      icon: Star,
      color: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      label: 'Shortlisted',
      bgGradient: 'from-yellow-50 to-yellow-100'
    },
    rejected: {
      icon: XCircle,
      color: 'text-red-600 bg-red-50 border-red-200',
      label: 'Not Selected',
      bgGradient: 'from-red-50 to-red-100'
    },
    interview_scheduled: {
      icon: Calendar,
      color: 'text-indigo-600 bg-indigo-50 border-indigo-200',
      label: 'Interview Scheduled',
      bgGradient: 'from-indigo-50 to-indigo-100'
    }
  };

  const config = statusConfig[application.status];
  const StatusIcon = config.icon;

  // Calculate days since application
  const daysSince = Math.floor(
    (new Date().getTime() - application.appliedAt.getTime()) / (1000 * 60 * 60 * 24)
  );

  return (
    <div
      onClick={onClick}
      className={`
        bg-gradient-to-br ${config.bgGradient} border rounded-xl p-5
        hover:shadow-lg transition-all cursor-pointer group
        ${config.color.split(' ')[2]}
      `}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="font-bold text-gray-900 text-lg group-hover:text-red-600 transition-colors">
            {application.jobTitle}
          </h3>
          {application.companyName && (
            <p className="text-sm text-gray-600 mt-1">{application.companyName}</p>
          )}
        </div>

        {/* Status Badge */}
        <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-full ${config.color} border`}>
          <StatusIcon className="w-4 h-4" />
          <span className="text-sm font-semibold">{config.label}</span>
        </div>
      </div>

      {/* Score Display (if test completed) */}
      {application.totalScore !== undefined && (
        <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Your Score</span>
            <div className="flex items-center space-x-2">
              <TrendingUp className={`w-4 h-4 ${
                application.totalScore >= 70 ? 'text-green-600' : 'text-orange-600'
              }`} />
              <span className={`text-2xl font-bold ${
                application.totalScore >= 70 ? 'text-green-600' : 'text-orange-600'
              }`}>
                {Math.round(application.totalScore)}%
              </span>
            </div>
          </div>

          {application.mcqScore !== undefined && application.interviewScore !== undefined && (
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div className="text-center">
                <p className="text-xs text-gray-500">MCQ</p>
                <p className="font-semibold text-gray-900">{Math.round(application.mcqScore)}%</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500">Interview</p>
                <p className="font-semibold text-gray-900">{Math.round(application.interviewScore)}%</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Timeline Preview */}
      <div className="flex items-center space-x-2 mb-3">
        {application.timeline.slice(0, 4).map((event, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full ${
              index === application.timeline.length - 1
                ? config.color.split(' ')[1]
                : 'bg-gray-300'
            }`}
            title={event.description}
          ></div>
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <div className="flex items-center space-x-1">
          <Clock className="w-3 h-3" />
          <span>{daysSince === 0 ? 'Today' : `${daysSince} day${daysSince !== 1 ? 's' : ''} ago`}</span>
        </div>

        <div className="flex items-center space-x-2">
          <span className="text-xs">{application.timeline.length} updates</span>
          <span className="text-xs text-gray-400">•</span>
          <span className="text-xs font-medium text-red-600 group-hover:underline">
            View Details →
          </span>
        </div>
      </div>
    </div>
  );
};

export default ApplicationStatusCard;
